import mammoth from "mammoth";
import ExcelJS from "exceljs";
import JSZip from "jszip";

export type FilledFormPreviewKind = "pdf" | "docx" | "xlsx" | "other";

export function detectFilledFormKind(
  name: string,
  contentType?: string
): FilledFormPreviewKind {
  const lower = name.toLowerCase();
  const ct = (contentType || "").toLowerCase();
  if (lower.endsWith(".pdf") || ct.includes("pdf")) return "pdf";
  if (lower.endsWith(".docx") || ct.includes("wordprocessingml")) return "docx";
  if (
    lower.endsWith(".xlsx") ||
    lower.endsWith(".xlsm") ||
    ct.includes("spreadsheet")
  ) {
    return "xlsx";
  }
  return "other";
}

export function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Copy into a standalone ArrayBuffer (avoids SharedArrayBuffer / offset issues). */
export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const bytes = base64ToUint8Array(base64);
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function objectUrlApi(targetWindow?: Window | null): typeof URL {
  const fromWindow = (targetWindow as unknown as { URL?: typeof URL } | null)
    ?.URL;
  return fromWindow ?? URL;
}

export function createPdfObjectUrl(
  contentBytes: string,
  contentType?: string,
  /** Use the popout window so blob: URLs work in its iframes. */
  targetWindow?: Window | null
): string {
  const bytes = base64ToUint8Array(contentBytes);
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  const blob = new Blob([copy.buffer], {
    type: contentType || "application/pdf",
  });
  return objectUrlApi(targetWindow).createObjectURL(blob);
}

export function revokeObjectUrl(
  url: string,
  targetWindow?: Window | null
): void {
  objectUrlApi(targetWindow).revokeObjectURL(url);
}

export function downloadBase64File(
  name: string,
  contentType: string,
  contentBytes: string,
  /** Prefer the popout document when the dialog is popped out. */
  targetDocument?: Document | null
): void {
  const bytes = base64ToUint8Array(contentBytes);
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  const blob = new Blob([copy], {
    type: contentType || "application/octet-stream",
  });
  const doc = targetDocument ?? document;
  const url = objectUrlApi(doc.defaultView).createObjectURL(blob);
  const a = doc.createElement("a");
  a.href = url;
  a.download = name || "download";
  a.style.display = "none";
  doc.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => objectUrlApi(doc.defaultView).revokeObjectURL(url), 1000);
}

function escapeHtmlText(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Collect all <w:t> text inside a Word XML fragment (handles nested runs). */
function wordXmlPlainText(xml: string): string {
  const parts: string[] = [];
  const re = /<w:t\b[^>]*>([^<]*)<\/w:t>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml)) !== null) {
    parts.push(m[1] ?? "");
  }
  return parts.join("").replace(/\u00a0/g, " ");
}

/**
 * Lightweight DOCX → HTML when mammoth fails (it throws "Not implemented"
 * on some nested Word XML nodes / field codes).
 */
async function docxBase64ToHtmlFallback(
  contentBytes: string
): Promise<string> {
  const zip = await JSZip.loadAsync(base64ToUint8Array(contentBytes));
  const docFile =
    zip.file("word/document.xml") ||
    zip.file("word/document2.xml");
  if (!docFile) {
    throw new Error("Invalid Word document (missing document.xml)");
  }
  const xml = await docFile.async("string");
  const bodyMatch = xml.match(/<w:body\b[^>]*>([\s\S]*?)<\/w:body>/);
  const body = bodyMatch?.[1] ?? xml;

  const parts: string[] = [];
  const chunkRe = /<(w:tbl|w:p)\b[\s\S]*?<\/\1>/g;
  let m: RegExpExecArray | null;
  while ((m = chunkRe.exec(body)) !== null) {
    const chunk = m[0];
    const tag = m[1];
    if (tag === "w:tbl") {
      const rows: string[] = [];
      const rowRe = /<w:tr\b[\s\S]*?<\/w:tr>/g;
      let rowMatch: RegExpExecArray | null;
      while ((rowMatch = rowRe.exec(chunk)) !== null) {
        rows.push(rowMatch[0]);
      }
      if (!rows.length) continue;
      const rowHtml = rows
        .map((rowXml) => {
          const cells: string[] = [];
          const cellRe = /<w:tc\b[\s\S]*?<\/w:tc>/g;
          let cellMatch: RegExpExecArray | null;
          while ((cellMatch = cellRe.exec(rowXml)) !== null) {
            const text = escapeHtmlText(wordXmlPlainText(cellMatch[0]).trim());
            cells.push(
              `<td style="border:1px solid #bbb;padding:4px 8px;vertical-align:top">${
                text || "&nbsp;"
              }</td>`
            );
          }
          return `<tr>${cells.join("")}</tr>`;
        })
        .join("");
      parts.push(
        `<table style="border-collapse:collapse;width:100%;margin:0.6em 0;font-size:12px">${rowHtml}</table>`
      );
    } else {
      const text = escapeHtmlText(wordXmlPlainText(chunk).trim());
      if (!text) continue;
      parts.push(`<p style="margin:0.35em 0">${text}</p>`);
    }
  }

  if (!parts.length) {
    const all = escapeHtmlText(wordXmlPlainText(body).trim());
    if (!all) return "<p><em>No previewable content.</em></p>";
    return `<p>${all.replace(/\n+/g, "</p><p>")}</p>`;
  }
  return `<div class="docx-preview-fallback">${parts.join("")}</div>`;
}

export async function docxBase64ToHtml(contentBytes: string): Promise<string> {
  try {
    const result = await mammoth.convertToHtml({
      arrayBuffer: base64ToArrayBuffer(contentBytes),
    });
    if (result.value?.trim()) {
      return result.value;
    }
  } catch {
    // Mammoth throws "Not implemented" on some complex Word XML; use fallback.
  }
  return docxBase64ToHtmlFallback(contentBytes);
}

/** ExcelJS cell values are often objects (rich text, formula, hyperlink). */
function excelCellValueToString(value: unknown): string {
  if (value == null || value === "") return "";
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  if (value instanceof Date) {
    return value.toLocaleString();
  }
  if (typeof value !== "object") return String(value);

  const v = value as Record<string, unknown>;
  if (Array.isArray(v.richText)) {
    return (v.richText as Array<{ text?: string }>)
      .map((part) => part.text ?? "")
      .join("");
  }
  if (typeof v.text === "string" || typeof v.text === "number") {
    const t = String(v.text);
    // Don't surface raw formula text in preview
    if (/^[=+]/.test(t.trim()) || /\bSUM\s*\(/i.test(t)) return "";
    return t;
  }
  if ("result" in v) {
    const result = excelCellValueToString(v.result);
    if (result !== "") return result;
    // Formula with no cached result — don't show "=SUM(...)" / "+I27*B27"
    return "";
  }
  if (typeof v.sharedFormula === "string") {
    return excelCellValueToString(v.result);
  }
  // Never dump raw formula strings into the preview
  if (typeof v.formula === "string") {
    return "";
  }
  if (typeof v.error === "string") {
    return v.error;
  }
  return "";
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Office default theme palette (RGB hex without #). */
const OFFICE_THEME_RGB = [
  "FFFFFF",
  "000000",
  "E7E6E6",
  "44546A",
  "4472C4",
  "ED7D31",
  "A5A5A5",
  "FFC000",
  "5B9BD5",
  "70AD47",
  "0563C1",
  "954F72",
];

function applyTint(rgbHex: string, tint: number): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  let r = parseInt(rgbHex.slice(0, 2), 16);
  let g = parseInt(rgbHex.slice(2, 4), 16);
  let b = parseInt(rgbHex.slice(4, 6), 16);
  if (tint < 0) {
    const f = 1 + tint;
    r = clamp(r * f);
    g = clamp(g * f);
    b = clamp(b * f);
  } else if (tint > 0) {
    r = clamp(r + (255 - r) * tint);
    g = clamp(g + (255 - g) * tint);
    b = clamp(b + (255 - b) * tint);
  }
  return `${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

function excelColorToCss(
  color?: Partial<ExcelJS.Color> | { theme?: number; tint?: number; argb?: string } | null
): string | undefined {
  if (!color) return undefined;
  let rgb = color.argb;
  if (rgb) {
    if (rgb.length === 8) {
      const a = parseInt(rgb.slice(0, 2), 16) / 255;
      const hex = rgb.slice(2);
      if (a < 1) {
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return `rgba(${r},${g},${b},${a})`;
      }
      return `#${hex}`;
    }
    if (rgb.length === 6) return `#${rgb}`;
  }
  if (typeof color.theme === "number") {
    const base =
      OFFICE_THEME_RGB[color.theme] ?? OFFICE_THEME_RGB[0] ?? "FFFFFF";
    const tint =
      "tint" in color && typeof color.tint === "number" ? color.tint : undefined;
    const tinted = tint != null ? applyTint(base, tint) : base;
    return `#${tinted}`;
  }
  return undefined;
}

function borderCss(
  side?: Partial<ExcelJS.Border>
): string | undefined {
  if (!side?.style) return undefined;
  const width =
    side.style === "medium" || side.style === "mediumDashed"
      ? "2px"
      : side.style === "thick"
        ? "3px"
        : "1px";
  const style =
    String(side.style).includes("dash") || side.style === "dotted"
      ? "dashed"
      : "solid";
  const color = excelColorToCss(side.color) || "#000";
  return `${width} ${style} ${color}`;
}

function decodeCellAddress(addr: string): { row: number; col: number } | null {
  const m = /^([A-Z]+)(\d+)$/i.exec(addr.trim());
  if (!m) return null;
  const letters = m[1].toUpperCase();
  const row = parseInt(m[2], 10);
  let col = 0;
  for (let i = 0; i < letters.length; i += 1) {
    col = col * 26 + (letters.charCodeAt(i) - 64);
  }
  return { row, col };
}

function parseMergeRange(
  range: string
): { r1: number; c1: number; r2: number; c2: number } | null {
  const parts = range.split(":");
  if (parts.length !== 2) return null;
  const a = decodeCellAddress(parts[0]);
  const b = decodeCellAddress(parts[1]);
  if (!a || !b) return null;
  return {
    r1: Math.min(a.row, b.row),
    c1: Math.min(a.col, b.col),
    r2: Math.max(a.row, b.row),
    c2: Math.max(a.col, b.col),
  };
}

function cellDisplayText(cell: ExcelJS.Cell): string {
  // Prefer structured value parsing so formulas don't render as "+I27*B27"
  const fromValue = excelCellValueToString(cell.value);
  if (fromValue !== "") return fromValue;
  try {
    const t = cell.text;
    if (t == null) return "";
    const s = String(t).trim();
    if (!s) return "";
    if (/^[=+]/.test(s) || /\bSUM\s*\(/i.test(s)) return "";
    return String(t);
  } catch {
    return "";
  }
}

function cellInlineStyle(cell: ExcelJS.Cell): string {
  const styles: string[] = [
    "padding:3px 6px",
    "vertical-align:top",
    "box-sizing:border-box",
  ];

  const fill = cell.fill;
  if (fill && fill.type === "pattern" && fill.pattern !== "none") {
    const bg =
      excelColorToCss(fill.fgColor) || excelColorToCss(fill.bgColor);
    if (bg) styles.push(`background:${bg}`);
  }

  const font = cell.font;
  if (font) {
    if (font.bold) styles.push("font-weight:700");
    if (font.italic) styles.push("font-style:italic");
    if (font.underline) styles.push("text-decoration:underline");
    if (font.size) styles.push(`font-size:${font.size}pt`);
    if (font.name) styles.push(`font-family:${JSON.stringify(font.name)}`);
    const fc = excelColorToCss(font.color);
    if (fc) styles.push(`color:${fc}`);
  } else {
    styles.push("font-size:10pt");
    styles.push('font-family:"Calibri",Arial,sans-serif');
  }

  const align = cell.alignment;
  if (align) {
    if (align.horizontal) styles.push(`text-align:${align.horizontal}`);
    if (align.vertical) {
      const v =
        align.vertical === "middle"
          ? "middle"
          : align.vertical === "bottom"
            ? "bottom"
            : "top";
      styles.push(`vertical-align:${v}`);
    }
    if (align.wrapText) styles.push("white-space:pre-wrap");
    else styles.push("white-space:pre");
  } else {
    styles.push("white-space:pre-wrap");
  }

  const border = cell.border;
  if (border) {
    const t = borderCss(border.top);
    const r = borderCss(border.right);
    const b = borderCss(border.bottom);
    const l = borderCss(border.left);
    if (t) styles.push(`border-top:${t}`);
    if (r) styles.push(`border-right:${r}`);
    if (b) styles.push(`border-bottom:${b}`);
    if (l) styles.push(`border-left:${l}`);
  }

  return styles.join(";");
}

/**
 * Render the first worksheet as styled HTML that approximates Excel (fills,
 * fonts, borders, merges, column widths) — not a plain text dump.
 */
function uint8ToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    const slice = bytes.subarray(i, i + chunk);
    for (let j = 0; j < slice.length; j += 1) {
      binary += String.fromCharCode(slice[j]!);
    }
  }
  return btoa(binary);
}

/** Map worksheet images to the top-left cell they float over (1-based). */
function worksheetImagesByCell(
  workbook: ExcelJS.Workbook,
  sheet: ExcelJS.Worksheet
): Map<string, string> {
  const byCell = new Map<string, string>();
  const media = (
    workbook as ExcelJS.Workbook & {
      media?: Array<{ extension?: string; buffer?: ArrayBuffer | Uint8Array }>;
    }
  ).media;
  if (!media?.length || typeof sheet.getImages !== "function") return byCell;

  for (const img of sheet.getImages()) {
    const imageId = Number(
      (img as { imageId?: number | string }).imageId
    );
    if (!Number.isFinite(imageId)) continue;
    const asset = media[imageId];
    if (!asset?.buffer) continue;
    const tl = img.range?.tl as
      | { nativeCol?: number; nativeRow?: number }
      | undefined;
    if (!tl || tl.nativeCol == null || tl.nativeRow == null) continue;
    const row = tl.nativeRow + 1;
    const col = tl.nativeCol + 1;
    const bytes =
      asset.buffer instanceof Uint8Array
        ? asset.buffer
        : new Uint8Array(asset.buffer);
    const ext = (asset.extension || "png").replace(/^\./, "");
    const mime =
      ext === "jpg" || ext === "jpeg"
        ? "image/jpeg"
        : ext === "gif"
          ? "image/gif"
          : "image/png";
    const dataUrl = `data:${mime};base64,${uint8ToBase64(bytes)}`;
    byCell.set(`${row}:${col}`, dataUrl);
  }
  return byCell;
}

/**
 * ExcelJS often misses zip-injected drawings (e.g. Susan Lewis signature PNG).
 * Parse drawing XML + media directly so preview matches the downloaded file.
 */
async function worksheetImagesFromZip(
  contentBytes: string
): Promise<Map<string, string>> {
  const byCell = new Map<string, string>();
  try {
    const zip = await JSZip.loadAsync(base64ToUint8Array(contentBytes));
    const drawingNames = Object.keys(zip.files)
      .filter((n) => /^xl\/drawings\/drawing\d+\.xml$/.test(n))
      .sort();
    for (const drawingPath of drawingNames) {
      const drawingFile = zip.file(drawingPath);
      if (!drawingFile) continue;
      const drawing = await drawingFile.async("string");
      const relsPath = drawingPath.replace(
        /drawings\/(drawing\d+)\.xml$/,
        "drawings/_rels/$1.xml.rels"
      );
      const relsFile = zip.file(relsPath);
      const ridToTarget: Record<string, string> = {};
      if (relsFile) {
        const rels = await relsFile.async("string");
        const relRe =
          /Id="(rId\d+)"[^>]*Target="([^"]+)"|Target="([^"]+)"[^>]*Id="(rId\d+)"/g;
        let rm: RegExpExecArray | null;
        while ((rm = relRe.exec(rels)) !== null) {
          const id = rm[1] || rm[4];
          const target = rm[2] || rm[3];
          if (id && target) ridToTarget[id] = target;
        }
      }

      const anchorRe = /<xdr:twoCellAnchor[\s\S]*?<\/xdr:twoCellAnchor>/g;
      let am: RegExpExecArray | null;
      while ((am = anchorRe.exec(drawing)) !== null) {
        const xml = am[0];
        // Skip ActiveX checkboxes — those are handled separately.
        if (/name="Check Box /i.test(xml)) continue;
        const rid = (xml.match(/r:embed="(rId\d+)"/) || [])[1];
        if (!rid) continue;
        const target = ridToTarget[rid];
        if (!target) continue;
        const mediaPath = target.startsWith("/")
          ? target.slice(1)
          : target.replace(/^\.\.\//, "xl/");
        const mediaFile = zip.file(mediaPath) || zip.file(`xl/media/${target.split("/").pop()}`);
        if (!mediaFile) continue;
        const col = Number((xml.match(/<xdr:from>\s*<xdr:col>(\d+)/) || [])[1]);
        const row = Number(
          (xml.match(/<xdr:from>[\s\S]*?<xdr:row>(\d+)/) || [])[1]
        );
        if (!Number.isFinite(col) || !Number.isFinite(row)) continue;
        const bytes = await mediaFile.async("uint8array");
        const lower = mediaPath.toLowerCase();
        const mime = lower.endsWith(".jpg") || lower.endsWith(".jpeg")
          ? "image/jpeg"
          : lower.endsWith(".gif")
            ? "image/gif"
            : "image/png";
        byCell.set(
          `${row + 1}:${col + 1}`,
          `data:${mime};base64,${uint8ToBase64(bytes)}`
        );
      }
    }
  } catch {
    // Preview without zip images is still usable
  }
  return byCell;
}

/**
 * ActiveX / form-control checkboxes are invisible to ExcelJS. Read them from
 * the xlsx zip so the preview can show ☑ / ☐ (e.g. Small Business / Woman-Owned).
 */
async function parseExcelFormCheckboxes(
  contentBytes: string
): Promise<Map<string, boolean>> {
  const byCell = new Map<string, boolean>();
  try {
    const zip = await JSZip.loadAsync(base64ToUint8Array(contentBytes));
    const drawingNames = Object.keys(zip.files)
      .filter((n) => /^xl\/drawings\/drawing\d+\.xml$/.test(n))
      .sort();
    const drawingFile = drawingNames[0] ? zip.file(drawingNames[0]) : null;
    if (!drawingFile) return byCell;
    const drawing = await drawingFile.async("string");

    const anchors = new Map<
      string,
      { row: number; col: number; hidden: boolean }
    >();
    const anchorRe = /<xdr:twoCellAnchor[\s\S]*?<\/xdr:twoCellAnchor>/g;
    let am: RegExpExecArray | null;
    while ((am = anchorRe.exec(drawing)) !== null) {
      const xml = am[0];
      const name = (xml.match(/name="(Check Box [^"]+)"/) || [])[1];
      if (!name) continue;
      const col = Number((xml.match(/<xdr:from>\s*<xdr:col>(\d+)/) || [])[1]);
      const row = Number(
        (xml.match(/<xdr:from>[\s\S]*?<xdr:row>(\d+)/) || [])[1]
      );
      if (!Number.isFinite(col) || !Number.isFinite(row)) continue;
      const hidden = /hidden="1"/.test(xml);
      const prev = anchors.get(name);
      if (!prev || (prev.hidden && !hidden)) {
        anchors.set(name, { row: row + 1, col: col + 1, hidden });
      }
    }

    const sheetXmlFile =
      zip.file("xl/worksheets/sheet1.xml") ||
      Object.keys(zip.files)
        .filter((n) => /^xl\/worksheets\/sheet\d+\.xml$/.test(n))
        .map((n) => zip.file(n))[0];
    if (!sheetXmlFile) return byCell;
    const sheetXml = await sheetXmlFile.async("string");
    const relsPath = sheetXmlFile.name.replace(
      /worksheets\/(sheet\d+)\.xml$/,
      "worksheets/_rels/$1.xml.rels"
    );
    const relsFile = zip.file(relsPath);
    if (!relsFile) return byCell;
    const rels = await relsFile.async("string");
    const ridToTarget: Record<string, string> = {};
    const relRe = /Id="(rId\d+)"[^>]*Target="([^"]+)"/g;
    let rm: RegExpExecArray | null;
    while ((rm = relRe.exec(rels)) !== null) {
      ridToTarget[rm[1]!] = rm[2]!;
    }

    const controlRe = /<control\b[^>]*\/?>/g;
    let cm: RegExpExecArray | null;
    while ((cm = controlRe.exec(sheetXml)) !== null) {
      const tag = cm[0];
      const name = (tag.match(/\bname="([^"]+)"/) || [])[1];
      const rid = (tag.match(/\br:id="([^"]+)"/) || [])[1];
      if (!name || !rid) continue;
      const anchor = anchors.get(name);
      if (!anchor) continue;
      const target = ridToTarget[rid];
      if (!target || !target.includes("ctrlProps/")) continue;
      const propPath = "xl/ctrlProps/" + target.split("ctrlProps/")[1];
      const propFile = zip.file(propPath);
      if (!propFile) continue;
      const propXml = await propFile.async("string");
      const checked = /checked="Checked"/i.test(propXml);
      byCell.set(`${anchor.row}:${anchor.col}`, checked);
    }
  } catch {
    // Preview without checkboxes is still usable
  }
  return byCell;
}

export async function xlsxBase64ToHtml(
  contentBytes: string
): Promise<{ sheetName: string; html: string }> {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(base64ToArrayBuffer(contentBytes));
  const sheet = workbook.worksheets[0];
  if (!sheet) {
    return { sheetName: "", html: "" };
  }

  const rowCount = Math.max(sheet.rowCount || 0, sheet.actualRowCount || 0);
  const colCount = Math.max(
    sheet.columnCount || 0,
    sheet.actualColumnCount || 0
  );
  if (rowCount === 0 || colCount === 0) {
    return { sheetName: sheet.name, html: "" };
  }

  const mergeMaster = new Map<
    string,
    { rowspan: number; colspan: number }
  >();
  const covered = new Set<string>();

  const mergeRanges: string[] =
    (sheet.model as { merges?: string[] } | undefined)?.merges ?? [];
  for (const range of mergeRanges) {
    const m = parseMergeRange(range);
    if (!m) continue;
    mergeMaster.set(`${m.r1}:${m.c1}`, {
      rowspan: m.r2 - m.r1 + 1,
      colspan: m.c2 - m.c1 + 1,
    });
    for (let r = m.r1; r <= m.r2; r += 1) {
      for (let c = m.c1; c <= m.c2; c += 1) {
        if (r === m.r1 && c === m.c1) continue;
        covered.add(`${r}:${c}`);
      }
    }
  }

  const imagesByCell = worksheetImagesByCell(workbook, sheet);
  const zipImages = await worksheetImagesFromZip(contentBytes);
  zipImages.forEach((dataUrl, key) => {
    if (!imagesByCell.has(key)) imagesByCell.set(key, dataUrl);
  });
  const checkboxAnchors = await parseExcelFormCheckboxes(contentBytes);
  // Pin marks to the option label. Anchors often sit in an empty col (or inside
  // the yellow "Business Size Type" merge A60:B61) left of "Small Business".
  const checkboxesByCell = new Map<string, boolean>();
  const isSectionHeader = (t: string) =>
    /business size type|business ownership|select one|select all that apply/i.test(
      t
    );
  checkboxAnchors.forEach((checked, key) => {
    const [rs, cs] = key.split(":");
    const r = Number(rs);
    const c = Number(cs);
    if (!Number.isFinite(r) || !Number.isFinite(c)) return;
    const same = cellDisplayText(sheet.getRow(r).getCell(c)).trim();
    const right = cellDisplayText(sheet.getRow(r).getCell(c + 1)).trim();
    let target = key;
    if (right && (!same || isSectionHeader(same))) {
      target = `${r}:${c + 1}`;
    } else if (same && !isSectionHeader(same)) {
      target = key;
    } else if (right) {
      target = `${r}:${c + 1}`;
    }
    if (checked || !checkboxesByCell.has(target)) {
      checkboxesByCell.set(target, checked);
    }
  });

  const colgroup: string[] = [];
  for (let c = 1; c <= colCount; c += 1) {
    const col = sheet.getColumn(c);
    const w = typeof col.width === "number" ? col.width : 10;
    // Approximate Excel column width → CSS px
    const px = Math.max(28, Math.round(w * 7.5 + 5));
    colgroup.push(`<col style="width:${px}px" />`);
  }

  const bodyRows: string[] = [];
  for (let r = 1; r <= rowCount; r += 1) {
    const row = sheet.getRow(r);
    const heightPt =
      typeof row.height === "number" ? row.height : undefined;
    const trStyle = heightPt
      ? ` style="height:${Math.round(heightPt * 1.33)}px"`
      : "";
    const cells: string[] = [];
    for (let c = 1; c <= colCount; c += 1) {
      const key = `${r}:${c}`;
      if (covered.has(key)) continue;

      const cell = row.getCell(c);
      const span = mergeMaster.get(key);
      const rowspan = span && span.rowspan > 1 ? ` rowspan="${span.rowspan}"` : "";
      const colspan = span && span.colspan > 1 ? ` colspan="${span.colspan}"` : "";
      const text = escapeHtml(cellDisplayText(cell));
      const style = cellInlineStyle(cell);
      const dataUrl = imagesByCell.get(key);
      const imgHtml = dataUrl
        ? `<div style="margin-top:2px"><img src="${dataUrl}" alt="" style="max-height:72px;max-width:220px;object-fit:contain" /></div>`
        : "";
      const box = checkboxesByCell.get(key);
      const boxHtml =
        box === undefined
          ? ""
          : `<span style="font-size:12pt;line-height:1;margin-right:4px;color:#111" title="${
              box ? "Checked" : "Unchecked"
            }">${box ? "☑" : "☐"}</span>`;
      const inner =
        boxHtml || text || imgHtml
          ? `${boxHtml}${text}${imgHtml}`
          : "&nbsp;";
      cells.push(`<td${rowspan}${colspan} style="${style}">${inner}</td>`);
    }
    bodyRows.push(`<tr${trStyle}>${cells.join("")}</tr>`);
  }

  const html = [
    `<div class="xlsx-preview-sheet" style="background:#fff;display:inline-block;min-width:100%">`,
    `<table style="border-collapse:collapse;table-layout:fixed;empty-cells:show;font-family:Calibri,Arial,sans-serif;font-size:10pt">`,
    `<colgroup>${colgroup.join("")}</colgroup>`,
    `<tbody>${bodyRows.join("")}</tbody>`,
    `</table>`,
    `</div>`,
  ].join("");

  return { sheetName: sheet.name, html };
}
